import React, { useEffect, useState, useRef, useCallback } from "react";
import { WifiOff, RefreshCw, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { count as queueCount, flush as queueFlush, subscribe as queueSubscribe } from "@/lib/offlineQueue";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

/**
 * Global connectivity + queued-writes banner.
 *
 * Shows when either:
 *   • the browser reports we're offline
 *   • there are queued offline writes waiting to sync
 *
 * Behavior:
 *   • Listens to window.online / window.offline events + a 30s sweeper.
 *   • Automatically flushes the queue every time we come back online.
 *   • Exposes a manual "Sync now" button so the user can retry immediately.
 *
 * Placement: rendered once inside <AppLayout> so it lives above the BottomNav
 * on every route.
 */
export default function OfflineBanner() {
  const [online, setOnline] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);

  const refreshCount = useCallback(async () => {
    try {
      const n = await queueCount();
      setPending(n);
    } catch { /* IndexedDB missing on some browsers — stay silent */ }
  }, []);

  const flushQueue = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      const res = await queueFlush(api);
      if (res.synced > 0) {
        toast.success(`Synced ${res.synced} offline item${res.synced === 1 ? "" : "s"}`);
      }
      if (res.failed > 0) {
        toast.error(`${res.failed} item${res.failed === 1 ? "" : "s"} couldn’t be synced`);
      }
    } catch (e) { /* swallow */ }
    finally {
      syncingRef.current = false;
      setSyncing(false);
      refreshCount();
    }
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
    const unsub = queueSubscribe((n) => setPending(n));

    const onOnline = () => {
      setOnline(true);
      // Small debounce so the browser has time to actually reach the server
      setTimeout(flushQueue, 800);
    };
    const onOffline = () => setOnline(false);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    // Periodic sweeper (only tries to flush when online + pending > 0)
    const iv = setInterval(() => {
      if (navigator.onLine) {
        queueCount().then((n) => {
          setPending(n);
          if (n > 0) flushQueue();
        }).catch(() => {});
      }
    }, 30000);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      clearInterval(iv);
      unsub?.();
    };
  }, [flushQueue, refreshCount]);

  // Hide if online and nothing pending
  if (online && pending === 0) return null;

  const label = !online
    ? (pending > 0 ? `Offline — ${pending} pending sync` : "You're offline")
    : (pending > 0 ? `${pending} item${pending === 1 ? "" : "s"} pending sync` : "");

  const tone = !online ? "warning" : "info";

  return (
    <div
      className={cn(
        "px-3 py-2 text-xs flex items-center gap-2 border-t border-b",
        tone === "warning"
          ? "bg-[hsl(var(--warning))]/12 border-[hsl(var(--warning))]/25 text-[hsl(var(--warning))]"
          : "bg-primary/10 border-primary/20 text-primary",
      )}
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
    >
      <WifiOff className="h-3.5 w-3.5 shrink-0" />
      <div className="flex-1 truncate font-medium">{label}</div>
      {online && pending > 0 && (
        <button
          type="button"
          onClick={flushQueue}
          disabled={syncing}
          className="inline-flex items-center gap-1 rounded-full border border-current px-2 py-0.5 text-[10px] hover:bg-current/10 disabled:opacity-50"
          data-testid="offline-banner-sync"
        >
          {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      )}
    </div>
  );
}
